/**
 * useActionState Hook
 *
 * Manages action execution state (loading, mobile detection).
 */

import { useState, useEffect } from "react";
import { MOBILE_BREAKPOINT } from "../types/action-rendering";

interface UseActionStateReturn {
  loadingActions: Set<string>;
  isMobile: boolean;
  setLoadingAction: (actionId: string, isLoading: boolean) => void;
}

/**
 * Hook for managing action execution state
 *
 * @returns Action state and handlers
 */
export function useActionState(): UseActionStateReturn {
  const [loadingActions, setLoadingActions] = useState<Set<string>>(
    () => new Set()
  );
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      // Use a more reliable mobile detection
      const isMobileDevice =
        window.innerWidth < MOBILE_BREAKPOINT ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const setLoadingAction = (actionId: string, isLoading: boolean) => {
    setLoadingActions((prev) => {
      const newSet = new Set(prev);
      if (isLoading) {
        newSet.add(actionId);
      } else {
        newSet.delete(actionId);
      }
      return newSet;
    });
  };

  return {
    loadingActions,
    isMobile,
    setLoadingAction,
  };
}
